package dev.kuml.portal.web.editor

/**
 * Pure zoom/pan math for the editor's diagram-viewer zoom (R-6) -- no DOM, so the scale-clamping
 * and pivot-anchored zoom formulas are directly unit-testable (see `ZoomMathTest`) instead of only
 * verifiable by eye in a browser.
 *
 * Ported (not copied -- different stack: CSS `transform` on a KVision `Div` wrapper around a
 * blob-`<img>`, instead of `obsidian-kuml`'s live SVG-DOM `stageEl` inside an Obsidian `Modal`)
 * from `obsidian-kuml`'s `KumlZoomModal` (`main.ts`), the reference implementation named in this
 * wave's plan. [MIN_SCALE]/[MAX_SCALE]/[ZOOM_STEP] are the same constants `KumlZoomModal` uses --
 * accepted as starting values for this wave, not re-derived.
 *
 * R-7 adds [canZoomIn]/[canZoomOut] on top of this: the zoom toolbar's "+"/"-" controls use them
 * to decide their own disabled state, so the enabled/disabled boundary can never drift from
 * [zoomTo]'s own `changed` flag.
 */
public object ZoomMath {
    public const val MIN_SCALE: Double = 0.25
    public const val MAX_SCALE: Double = 4.0
    public const val ZOOM_STEP: Double = 1.2

    /** Multiplicative slack above `fitScale` at which panning/the reset control become active --
     * not a bare `scale > fitScale`, so floating-point noise right at "fit" can't flicker pan
     * on/off. Mirrors `KumlZoomModal`'s `this.scale > this.fitScale * 1.0001`. */
    private const val PAST_FIT_SLACK: Double = 1.0001

    /**
     * True contain-fit of the diagram's native pixel size within the viewport's current pixel
     * box. Falls back to `1.0` if either box is degenerate (zero/negative width or height), which
     * only happens before the stage has been laid out or before the image has reported its
     * natural size -- never clamped by [clampScale] here, only capped at [MAX_SCALE], so a tiny
     * diagram doesn't upscale past what manual zoom-in itself allows, mirroring `zoomFit()`.
     */
    public fun computeFitScale(
        viewportWidth: Double,
        viewportHeight: Double,
        naturalWidth: Double,
        naturalHeight: Double,
    ): Double {
        if (viewportWidth <= 0 || viewportHeight <= 0 || naturalWidth <= 0 || naturalHeight <= 0) return 1.0
        val containScale = minOf(viewportWidth / naturalWidth, viewportHeight / naturalHeight)
        return minOf(MAX_SCALE, containScale)
    }

    /**
     * Clamps a candidate scale to `[min(MIN_SCALE, fitScale), MAX_SCALE]`. The lower bound
     * relaxes below [MIN_SCALE] for a diagram so huge that [computeFitScale] itself had to go
     * smaller than [MIN_SCALE] to show it completely -- manual zoom-out must stay able to reach
     * fit, instead of snapping back in to [MIN_SCALE] (a jarring inconsistency with what "Fit"
     * just showed).
     */
    public fun clampScale(
        scale: Double,
        fitScale: Double,
    ): Double {
        val lowerBound = minOf(MIN_SCALE, fitScale)
        return minOf(MAX_SCALE, maxOf(lowerBound, scale))
    }

    /** Whether drag-to-pan (and the reset control's "not at fit" visibility) should be active at
     * the given scale. */
    public fun canPan(
        scale: Double,
        fitScale: Double,
    ): Boolean = scale > fitScale * PAST_FIT_SLACK

    /**
     * Result of a pivot-anchored zoom: the clamped new scale/translate, and whether anything
     * actually changed ([changed] is `false` when the candidate scale clamps to the value already
     * active -- callers should skip re-applying the CSS transform in that case, mirroring
     * `KumlZoomModal.zoomTo`'s early `if (next === prev) return`).
     */
    public data class ZoomResult(
        val scale: Double,
        val translateX: Double,
        val translateY: Double,
        val changed: Boolean,
    )

    /**
     * Zoom around a pivot point (e.g. the pointer position on a wheel notch) so the diagram point
     * currently under the pivot stays under it after the scale change -- the "anchored" wheel-zoom
     * feel. [pivotX]/[pivotY] and the returned translate are both in the same reference frame (the
     * viewport's own top-left-relative pixel coordinates, i.e. `clientX - viewportRect.left`); a
     * caller with no natural pivot (a reset/fit control) should pass the viewport's own center.
     */
    public fun zoomTo(
        currentScale: Double,
        currentTranslateX: Double,
        currentTranslateY: Double,
        candidateScale: Double,
        fitScale: Double,
        pivotX: Double,
        pivotY: Double,
    ): ZoomResult {
        val next = clampScale(candidateScale, fitScale)
        if (next == currentScale) {
            return ZoomResult(currentScale, currentTranslateX, currentTranslateY, changed = false)
        }
        // Keep the point under the pivot fixed: solve for the new translate.
        // stagePoint = (pivot - translate) / prevScale ; newTranslate = pivot - stagePoint * next
        val stageX = (pivotX - currentTranslateX) / currentScale
        val stageY = (pivotY - currentTranslateY) / currentScale
        val newTranslateX = pivotX - stageX * next
        val newTranslateY = pivotY - stageY * next
        return ZoomResult(next, newTranslateX, newTranslateY, changed = true)
    }

    /**
     * Whether a zoom-in click would change anything -- i.e. whether the toolbar's "+" control (R-7)
     * should be enabled. Deliberately expressed as "does [clampScale] move the scale at all?"
     * rather than a separate epsilon comparison against [MAX_SCALE], so the enabled-state and
     * [zoomTo]'s own `changed` flag can never disagree about where the zoom range ends.
     */
    public fun canZoomIn(
        scale: Double,
        fitScale: Double,
    ): Boolean = clampScale(scale * ZOOM_STEP, fitScale) != scale

    /** [canZoomIn]'s counterpart for the toolbar's "-" control. Honours the relaxed lower bound
     * [clampScale] applies for a diagram whose fit scale is itself below [MIN_SCALE]. */
    public fun canZoomOut(
        scale: Double,
        fitScale: Double,
    ): Boolean = clampScale(scale / ZOOM_STEP, fitScale) != scale
}
